import React, {
  useContext,
  useLayoutEffect,
  useRef,
  useMemo,
  useState,
} from "react";
import { Grid, Table, Image, Accordion, Icon } from "semantic-ui-react";

import Utility from "../../../Utility";
import GlobalContext from "../../../contexts/GlobalContext";
import { OptionPriceMode } from "../../../common/types/supplement";

import AmountProvider from "../../../components/Provider/AmountProvider";
import MomentCustomer from "../../../components/MomentCustomer/MomentCustomer";
import useResponsive from "../../../hooks/useResponsive";

const OptionDetails = ({ supplement, booking }) => {
  const [globalContext] = useContext(GlobalContext) as any;
  const contentRef = useRef(null);
  const { isTabletOrMobile, isMobile } = useResponsive();

  useLayoutEffect(() => {
    contentRef.current.scrollIntoView();
  }, []);

  const optionsPriceModeLabels = {
    [OptionPriceMode.Unique]: globalContext.translations.unique,
    [OptionPriceMode.ParJour]: globalContext.translations.perDay,
    [OptionPriceMode.ParPers]: globalContext.translations.perPers,
    [OptionPriceMode.JourPers]: globalContext.translations.perDayPers,
    [OptionPriceMode.Semaine]: globalContext.translations.week,
    [OptionPriceMode.SemainePers]: globalContext.translations.weekPers,
    [OptionPriceMode.PourcJour]: globalContext.translations.pourcDay,
    [OptionPriceMode.PourcJourHt]: globalContext.translations.pourcDayHt,
    [OptionPriceMode.PourcJourPers]: globalContext.translations.pourcDayPers,
  };

  return (
    <Grid columns="equal" padded>
      <Grid.Column textAlign="center" computer={6} mobile={16}>
        <Image ui centered src={Utility.imageExists(supplement.image)} />
      </Grid.Column>
      <Grid.Column computer={10} mobile={16}>
        <div ref={contentRef}>
          <div>
            <h3>{supplement.name}</h3>
            <span className="price-type">
              {globalContext.translations.priceMode} :&nbsp;
            </span>
            <small>{optionsPriceModeLabels?.[supplement.priceMode]}</small>
            {(supplement.priceMode === OptionPriceMode.Unique ||
              supplement.priceMode === OptionPriceMode.ParJour) && (
              <div>
                <span className="price-type">
                  {globalContext.translations.unitPrice} :&nbsp;
                </span>
                <small>
                  <AmountProvider
                    amount={
                      supplement.priceDetails.per_day?.[booking.arrivalDate]
                        ?.pax?.adult
                    }
                    currency={supplement.currency ?? booking.currency}
                  />
                </small>
              </div>
            )}
            <p className="mt-2 description-text">
              {supplement.description === "" ? " - " : supplement.description}
            </p>
            {supplement.priceMode === OptionPriceMode.Unique && (
              <div className="text-right mt-5">
                <h3>
                  <span style={{ marginLeft: 15 }}>
                    {globalContext.translations.total}:{" "}
                    <AmountProvider
                      amount={supplement.price}
                      currency={supplement.currency ?? booking.currency}
                    />
                  </span>
                </h3>
              </div>
            )}
          </div>
        </div>
      </Grid.Column>
      {supplement.priceMode !== OptionPriceMode.Unique && (
        <Grid.Column>
          <OptionDetailsLayout
            isMobile={isMobile}
            table={
              !isTabletOrMobile ||
              (isTabletOrMobile &&
                supplement.priceMode !== OptionPriceMode.JourPers)
            }
          >
            {supplement.priceMode === OptionPriceMode.ParPers && (
              <ParPersTableContent
                translations={globalContext.translations}
                booking={booking}
                supplement={supplement}
              />
            )}
            {supplement.priceMode === OptionPriceMode.ParJour && (
              <ParJourTableContent
                translations={globalContext.translations}
                booking={booking}
                supplement={supplement}
              />
            )}
            {supplement.priceMode === OptionPriceMode.JourPers && (
              <JourPersContent
                translations={globalContext.translations}
                booking={booking}
                supplement={supplement}
              />
            )}
            {supplement.priceMode === OptionPriceMode.Semaine && (
              <ParJourTableContent
                translations={globalContext.translations}
                booking={booking}
                supplement={supplement}
              />
            )}
            {supplement.priceMode === OptionPriceMode.SemainePers && (
              <JourPersContent
                translations={globalContext.translations}
                booking={booking}
                supplement={supplement}
              />
            )}
            {(supplement.priceMode === OptionPriceMode.PourcJourHt ||
              supplement.priceMode === OptionPriceMode.PourcJour) && (
              <ParJourTableContent
                translations={globalContext.translations}
                booking={booking}
                supplement={supplement}
              />
            )}
            {supplement.priceMode === OptionPriceMode.PourcJourPers && (
              <JourPersContent
                translations={globalContext.translations}
                booking={booking}
                supplement={supplement}
              />
            )}
          </OptionDetailsLayout>
        </Grid.Column>
      )}
    </Grid>
  );
};

const OptionDetailsLayout = ({ children, table = false, isMobile }) => {
  return (
    <>
      {table ? (
        <Table
          textAlign="center"
          compact="very"
          size="small"
          unstackable
          style={{ fontSize: isMobile ? "0.85em" : "0.95em" }}
        >
          {children}
        </Table>
      ) : (
        <div>{children}</div>
      )}
    </>
  );
};

// ------------------------------------------------------------------------------------------

const PaxDetailsRow = ({ supplement, translations, date, booking }) => {
  return (
    <>
      {Object.keys(supplement.priceDetails.per_day?.[date]?.pax).map(
        (paxType) => {
          if (!booking.pax[paxType] || parseInt(booking.pax[paxType]) < 1)
            return;

          const nbPax = supplement.pax[`${paxType}Number`];
          return (
            <React.Fragment key={paxType}>
              <Table.Row>
                <Table.Cell>
                  {translations[paxType]} x {nbPax}
                </Table.Cell>
                <Table.Cell>
                  <AmountProvider
                    amount={supplement.priceDetails.per_day[date].pax[paxType]}
                    currency={supplement.currency}
                  />
                </Table.Cell>
                <Table.Cell>
                  <AmountProvider
                    amount={
                      nbPax * supplement.priceDetails.per_day[date].pax[paxType]
                    }
                    currency={supplement.currency}
                  />
                </Table.Cell>
              </Table.Row>
            </React.Fragment>
          );
        }
      )}
    </>
  );
};

// ------------------------------------------------------------------------------------------

const ParJourTableContent = ({ booking, translations, supplement }) => {
  return (
    <>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{translations.days}</Table.HeaderCell>
          <Table.HeaderCell>{translations.unitPrice}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {Object.keys(supplement.priceDetails.per_day).map((date) => (
          <Table.Row key={date}>
            <Table.Cell>
              <MomentCustomer>{date}</MomentCustomer>
            </Table.Cell>
            <Table.Cell>
              <AmountProvider
                amount={supplement.priceDetails.per_day?.[date]?.pax?.adult}
                currency={supplement.currency ?? booking.currency}
              />
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
      <Table.Footer>
        <Table.Row>
          <Table.HeaderCell colSpan="2" textAlign="right">
            <h4>
              {translations.total}:{" "}
              <AmountProvider
                amount={supplement.price}
                currency={supplement.currency ?? booking.currency}
              />
            </h4>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Footer>
    </>
  );
};

// ------------------------------------------------------------------------------------------

const JourPersRow = ({ date, translations, supplement, booking }) => {
  const nbRow = useMemo(() => {
    return Object.keys(booking.pax).reduce((acc, val) => {
      if (booking.pax[val] > 0) {
        return acc + 1;
      }
      return acc;
    }, 1);
  }, []);

  return (
    <>
      <Table.Row>
        <Table.Cell rowSpan={nbRow}>
          <MomentCustomer>{date}</MomentCustomer>
        </Table.Cell>
      </Table.Row>
      <PaxDetailsRow
        date={date}
        translations={translations}
        supplement={supplement}
        booking={booking}
      />
    </>
  );
};

const JourPersContent = ({ translations, booking, supplement }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const { isTabletOrMobile, isMobile } = useResponsive();

  const handleClick = (_, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;
    setActiveIndex(newIndex);
  };

  const fontSize = isMobile ? "0.85em" : "0.95em";

  return (
    <>
      {isTabletOrMobile ? (
        <>
          <Accordion fluid styled>
            {Object.keys(supplement.priceDetails.per_day).map((date, index) => {

              const totalDayPrice = Object.keys(
                supplement.priceDetails.per_day[date]['pax']
              ).reduce((acc, val) => {
                const nbPax = supplement.pax[`${val}Number`];
                if (!nbPax) return acc;
                
                const price = parseFloat(supplement.priceDetails.per_day[date]["pax"][val])
                return acc + nbPax * price;
              }, 0);

              return (
                <React.Fragment key={date}>
                  <Accordion.Title
                    active={activeIndex === index}
                    index={index}
                    onClick={handleClick}
                  >
                    <Grid style={{fontSize}}>
                      <Grid.Row columns={3}>
                        <Grid.Column width={2}>
                          <Icon name="dropdown" />
                        </Grid.Column>
                        <Grid.Column width={7}>
                          <MomentCustomer>{date}</MomentCustomer>
                        </Grid.Column>
                        <Grid.Column width={7} textAlign="right">
                          <AmountProvider
                            amount={totalDayPrice}
                            currency={supplement.currency ?? booking.currency}
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Accordion.Title>
                  <Accordion.Content
                    style={{ fontSize }}
                    active={activeIndex === index}
                  >
                    <Table
                      basic="very"
                      textAlign="center"
                      compact="very"
                      size="small"
                      unstackable
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>
                            {translations.occupancy}
                          </Table.HeaderCell>
                          <Table.HeaderCell>
                            {translations.unitPrice}
                          </Table.HeaderCell>
                          <Table.HeaderCell>
                            {translations.price}
                          </Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        <PaxDetailsRow
                          date={booking.arrivalDate}
                          translations={translations}
                          supplement={supplement}
                          booking={booking}
                        />
                      </Table.Body>
                    </Table>
                  </Accordion.Content>
                </React.Fragment>
              );
            })}
          </Accordion>
          <h4 style={{ textAlign: "right" }}>
            <AmountProvider
              amount={supplement.price}
              currency={supplement.currency ?? booking.currency}
            />
          </h4>
        </>
      ) : (
        <>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{translations.days}</Table.HeaderCell>
              <Table.HeaderCell>{translations.occupancy}</Table.HeaderCell>
              <Table.HeaderCell>{translations.unitPrice}</Table.HeaderCell>
              <Table.HeaderCell>{translations.price}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {Object.keys(supplement.priceDetails.per_day).map((date) => (
              <JourPersRow
                key={date}
                date={date}
                supplement={supplement}
                translations={translations}
                booking={booking}
              />
            ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan="4" textAlign="right">
                <h4>
                  <AmountProvider
                    amount={supplement.price}
                    currency={supplement.currency ?? booking.currency}
                  />
                </h4>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </>
      )}
    </>
  );
};

// ------------------------------------------------------------------------------------------

const ParPersTableContent = ({ translations, supplement, booking }) => {
  return (
    <>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{translations.occupancy}</Table.HeaderCell>
          <Table.HeaderCell>{translations.unitPrice}</Table.HeaderCell>
          <Table.HeaderCell>{translations.price}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        <PaxDetailsRow
          date={booking.arrivalDate}
          translations={translations}
          supplement={supplement}
          booking={booking}
        />
      </Table.Body>
      <Table.Footer>
        <Table.Row>
          <Table.HeaderCell colSpan="3" textAlign="right">
            <h4>
              <AmountProvider
                amount={supplement.price}
                currency={supplement.currency ?? booking.currency}
              />
            </h4>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Footer>
    </>
  );
};

export default OptionDetails;
